import { Head, Link } from "@inertiajs/react"
import { Badge, Card, Code, Flex, Heading, Table } from "@radix-ui/themes"
import { z } from "zod"

import ProjectLayout from "@/layouts/project-layout"
import { projectDashboardPath } from "@/routes"
import { ProjectSchema, EventSchema, CustomerSchema, EventTypeSchema } from "@/schemas"
import { countKeys } from "@/lib/objects"
import { projectCustomerPath } from "@/routes"
import { formatDateTime } from "@/lib/datetime"

const PropsSchema = z.object({
  project: ProjectSchema,
  events: z.array(
    EventSchema.extend({
      customer: CustomerSchema.optional(),
    }),
  ),
  event_types: z.array(EventTypeSchema),
  filtered_event_type: z.string().nullable().optional(),
})

type Props = z.infer<typeof PropsSchema>

export default function EventsIndex(props: Props) {
  console.log(props)

  const validatedProps = PropsSchema.parse(props)
  const { project, events, event_types, filtered_event_type } = validatedProps

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Events" },
      ]}
    >
      <Head title="Events" />

      <Flex direction="column" gap="6" p="6">
        <Heading size="6" weight="medium">
          Events
        </Heading>

        {event_types.length > 0 && (
          <Flex justify="between" align="center">
            <Flex gap="2" wrap="wrap">
              {event_types.map((eventType) => {
                const isActive = filtered_event_type === eventType.event_name

                return (
                  <Link
                    key={eventType.id}
                    href={`/projects/${project.slug}/events?event_type=${encodeURIComponent(eventType.event_name)}`}
                  >
                    <Badge
                      color="blue"
                      variant={isActive ? "solid" : "soft"}
                      style={{ cursor: "pointer" }}
                    >
                      {eventType.event_name}: {eventType.count}
                    </Badge>
                  </Link>
                )
              })}
            </Flex>

            {filtered_event_type && (
              <Link href={`/projects/${project.slug}/events`}>
                <Badge color="red" variant="soft" style={{ cursor: "pointer" }}>
                  × Clear filter
                </Badge>
              </Link>
            )}
          </Flex>
        )}

        <Card>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Event Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Customer Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Custom Fields</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {events.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5} style={{ textAlign: "center", color: "var(--gray-11)" }}>
                    No events found
                  </Table.Cell>
                </Table.Row>
              ) : (
                events.map((event) => {
                  return (
                    <Table.Row key={event.id}>
                      <Table.RowHeaderCell>{event.name}</Table.RowHeaderCell>
                      <Table.Cell>
                        {event.customer ? (
                          <Link
                            href={projectCustomerPath(project.slug, event.customer.id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {event.customer.email}
                          </Link>
                        ) : (
                          "N/A"
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {event.is_test ? (
                          <Badge color="orange" variant="soft">
                            Test
                          </Badge>
                        ) : (
                          <Badge color="blue" variant="soft">
                            Live
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {countKeys(event.custom_fields) > 0 ? (
                          <Code
                            size="1"
                            style={{ display: "block", maxWidth: "300px", overflow: "auto" }}
                          >
                            {JSON.stringify(event.custom_fields, null, 2)}
                          </Code>
                        ) : (
                          <span style={{ color: "var(--gray-11)", fontStyle: "italic" }}>
                            No custom fields
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>{formatDateTime(event.created_at)}</Table.Cell>
                    </Table.Row>
                  )
                })
              )}
            </Table.Body>
          </Table.Root>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
